// MUI v6 — дефолты компонентов в стиле Material Design 3.
// Button (filled, rounded), Card (elevation 1), Chip (outlined), TextField (outlined),
// Dialog (maxWidth=sm), AppBar (sticky, surface).
import type { Components, Theme } from '@mui/material/styles';

/** Кастомизация MUI компонентов под MD3. */
export const md3Components: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    defaultProps: { variant: 'contained', disableElevation: false },
    styleOverrides: {
      root: { borderRadius: 20, textTransform: 'none', fontWeight: 500 },
    },
  },
  MuiCard: {
    defaultProps: { variant: 'elevation', elevation: 1 },
  },
  MuiChip: {
    defaultProps: { variant: 'outlined', size: 'small' },
    styleOverrides: {
      root: { borderRadius: 8 },
    },
  },
  MuiTextField: {
    defaultProps: { variant: 'outlined', size: 'medium', fullWidth: true },
  },
  MuiDialog: {
    defaultProps: { maxWidth: 'sm', fullWidth: true },
  },
  MuiAppBar: {
    defaultProps: { position: 'sticky', color: 'default' },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: 20 },
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 1 },
  },
  // U-33 (sled-V11) + V15-b (Bug-Fix V15): адаптация UA-автозаполнения Chrome к теме.
  // Chrome красит autofill-поля UA-фоном #e8f0fe (переживает author-стили) и чёрным текстом;
  // маскировка — НЕпрозрачный inset-box-shadow цветом фона формы (background.paper) + долгая
  // transition фона; текст/каретка — text.primary. styleOverrides — callback от theme
  // (цвета из палитры light/dark; theme.palette.* напрямую — theme.vars в билде отсутствует).
  // Селекторы: input-тег И .MuiInputBase-input (страхует все MUI-поля).
  MuiCssBaseline: {
    styleOverrides: (theme: Theme) => {
      // Поверхность за полем: модалки/карточки — background.paper. UA-фон #e8f0fe
      // маскируется только НЕпрозрачной тенью (transparent — U-33 дефект, не маскирует).
      const surface = theme.palette.background.paper;
      const text = theme.palette.text.primary;
      const autofillRules = {
        WebkitBoxShadow: `0 0 0 1000px ${surface} inset`,
        WebkitTextFillColor: text,
        caretColor: text,
        transition: 'background-color 5000s ease-in-out 0s',
      } as const;
      return {
        'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active':
          autofillRules,
        '.MuiInputBase-input:-webkit-autofill, .MuiInputBase-input:-webkit-autofill:hover, .MuiInputBase-input:-webkit-autofill:focus, .MuiInputBase-input:-webkit-autofill:active':
          autofillRules,
        // Label MUI не всегда «поднимается» при autofill (события input не всегда
        // приходят в React) — принудительно прячем label в shrink-позицию для outlined.
        '.MuiOutlinedInput-root:has(input:-webkit-autofill) .MuiInputLabel-outlined, .MuiOutlinedInput-root:has(input:-webkit-autofill:focus) .MuiInputLabel-outlined':
          { transform: 'translate(14px, -9px) scale(0.75)' },
      };
    },
  },
};
